import { ComponentType, FC, PropsWithChildren, ReactNode, useEffect, useState } from 'react'

import { safeStorage } from '@ps/tools/helpers/safeStorage'
import { asyncWithLDProvider, useLDClient } from 'launchdarkly-react-client-sdk'
import { z } from 'zod'

import { type AuthStoreType, useAuthStore } from '../../auth/hooks/useAuthStore'
import { IdentityService } from '../../auth/services/IdentityService'
import { BETA_TOGGLE_KEYS } from '../featureFlags'

const getBetaFeaturesToggleContext = (): Record<string, boolean | null> => {
  const betaToggleContext: Record<string, boolean | null> = {}

  /**
   * Get all beta toggle values from localStorage
   */
  Object.values(BETA_TOGGLE_KEYS).forEach((key) => {
    betaToggleContext[key] = safeStorage.get(key, z.boolean())
  })

  return betaToggleContext
}

const getLaunchDarklyContext = (identityService: IdentityService, user?: AuthStoreType | null) => ({
  ...identityService.getUserContext(user),
  ...getBetaFeaturesToggleContext(),
})

export interface LaunchDarklyUserContext {
  key: string
  kind?: 'user'
  anonymous?: boolean
  email?: string
  name?: string
  firstName?: string
  lastName?: string
  avatar?: string
  ip?: string
  country?: string
  // Custom attributes - add any additional properties you need
  [key: string]: string | number | boolean | undefined
}

type LaunchDarklyProviderProps = PropsWithChildren<{
  clientSideId: string
}>

/**
 * Rendered inside the LaunchDarkly provider tree so that useLDClient() resolves
 * a real LD context. Re-identifies the user whenever their id or email changes,
 * covering both the anonymous→authenticated transition and cases where email is
 * set asynchronously after the initial id (e.g. fetched from a settings API).
 */
const LDUserIdentifier: FC = () => {
  const ldClient = useLDClient()
  const userId = useAuthStore((state) => state.id)
  const userEmail = useAuthStore((state) => state.email)

  useEffect(() => {
    if (!ldClient || !userId) return

    const identityService = IdentityService.getInstance()
    const user = useAuthStore.getState()
    identityService.refreshIdentity(user)

    ldClient.identify(getLaunchDarklyContext(identityService, user))
  }, [userId, userEmail, ldClient])

  return null
}

/**
 * LaunchDarkly Provider Component
 *
 * Wraps the application with LaunchDarkly context.
 * Automatically identifies users based on authentication state or custom user context.
 *
 * @example
 * Basic usage with auto user ID:
 * ```tsx
 * import { LaunchDarklyProvider } from '@ps/ui';
 *
 * const clientSideId = import.meta.env.VITE_LAUNCHDARKLY_CLIENT_ID;
 *
 * <LaunchDarklyProvider clientSideId={clientSideId}>
 *   <App />
 * </LaunchDarklyProvider>
 * ```
 *
 * https://launchdarkly.com/docs/sdk/client-side/react/react-web
 */
export const LaunchDarklyProvider: FC<LaunchDarklyProviderProps> = ({ children, clientSideId }) => {
  const [LDProvider, setLDProvider] = useState<ComponentType<{
    children: ReactNode
  }> | null>(null)
  const [failedToInitialize, setFailedToInitialize] = useState(false)

  useEffect(() => {
    let cancelled = false

    const identityService = IdentityService.getInstance()
    const user = useAuthStore.getState()

    asyncWithLDProvider({
      clientSideID: clientSideId,
      context: getLaunchDarklyContext(identityService, user),
      timeout: 2, // in seconds
      options: { bootstrap: 'localStorage' },
    })
      .then((provider) => {
        if (!cancelled) {
          setLDProvider(() => provider)
          setFailedToInitialize(false)
        }
      })
      .catch((error) => {
        // TODO: Log in sentry
        console.error('Error initializing LaunchDarkly:', error)
        if (!cancelled) {
          setFailedToInitialize(true)
        }
      })

    return () => {
      cancelled = true
    }
  }, [clientSideId])

  if (!LDProvider) {
    /**
     * The page needs to wait for LaunchDarkly to initialize before rendering children
     * This will prevent any flickering of flags and ensure that the correct flag values are used on initial render
     *
     * For the page to load before LaunchDarkly initializes we need to
     * have loading states for every flag that is used in the app and it up to wait for LD to load
     *
     * To avoid complexity at this point we will wait for LD to initialize, which should take ms
     *
     * To avoid blocking the page if LD fails to initialize we will render the children without LD context
     * The flags should use the fallback values and users should not be impacted while we fix the issue
     */
    if (failedToInitialize) {
      return <>{children}</>
    }

    return <></>
  }

  return (
    <LDProvider>
      <LDUserIdentifier />
      {children}
    </LDProvider>
  )
}

// Re-export hooks from LaunchDarkly SDK for convenience
export { useFlags, useLDClient } from 'launchdarkly-react-client-sdk'
